import {
  Stack,
  Group,
  Button,
  Title,
  Loader,
  ActionIcon,
  Text,
  Space,
  ThemeIcon,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import {
  IconArrowLeft,
  IconArrowRight,
  IconCloudX,
  IconRosetteDiscountCheck,
  IconRefresh,
} from "@tabler/icons-react"

import { GCSDatasouceCreationFormData } from "@costory/types/endpoints/metricsDatasources"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { queryClient } from "@costory/front/lib/queryClient"
import { useValidateGCS } from "@costory/front/queries/metricsDatasources"

const REQUIRED_HEADERS = ["bucket", "project", "name", "size", "storageClass"]

export const GCSQueryValidation = ({
  form,
  handleClickBack,
  handleClickImport,
  isImporting,
}: {
  form: UseFormReturnType<GCSDatasouceCreationFormData>
  handleClickBack: () => void
  handleClickImport: () => void
  isImporting: boolean
}) => {
  const query = useValidateGCS({
    inventoryFilePath: form.values.inventoryFilePath,
    bucketName: form.values.bucketName,
    tagSearchPattern: form.values.tagSearchPattern,
  })

  const validateHeaders = (headers?: string[]) => {
    if (!headers) return false
    return REQUIRED_HEADERS.every((header) => headers.includes(header))
  }

  return (
    <Stack justify="space-between" h="100%">
      <Stack>
        <QueryWrapper
          query={query}
          LoadingComponent={
            <Stack align="center">
              <Loader size="xl" />
              <Title order={2}>Validating Inventory Parquet file...</Title>
            </Stack>
          }
          allowEmptyArray
        >
          {({ data }) => {
            if (!data) {
              return (
                <Stack align="center" justify="center" flex={1}>
                  <ThemeIcon
                    color="textSecondary"
                    variant="transparent"
                    size={120}
                    radius="xl"
                  >
                    <IconCloudX size={120} />
                  </ThemeIcon>

                  <Title order={2}>Failed to validate Inventory file</Title>

                  <Space h={10} />
                  <Group justify="flex-end">
                    <Text>Retry</Text>
                    <ActionIcon
                      loading={query.isLoading}
                      onClick={() =>
                        queryClient.resetQueries({
                          queryKey: ["validateGCS"],
                        })
                      }
                    >
                      <IconRefresh />
                    </ActionIcon>
                  </Group>
                </Stack>
              )
            }

            const headers = data.headers
            const hasValidHeaders = validateHeaders(headers)
            const isSuccess = data.isSuccess ?? false

            if (isSuccess && hasValidHeaders) {
              return (
                <Group justify="center">
                  <Stack align="center" gap="md" w="100%" maw={600}>
                    <ThemeIcon
                      color="success"
                      variant="transparent"
                      size={120}
                      radius="xl"
                    >
                      <IconRosetteDiscountCheck size={120} />
                    </ThemeIcon>
                    <Title order={2}>Your GCS inventory file is valid</Title>
                  </Stack>
                </Group>
              )
            }
            return (
              <Stack align="center" justify="center" flex={1}>
                <ThemeIcon
                  color="textSecondary"
                  variant="transparent"
                  size={120}
                  radius="xl"
                >
                  <IconCloudX size={120} />
                </ThemeIcon>

                <Title order={2}>
                  {data.errorCode
                    ? data.errorCode
                    : "Invalid GCS inventory file"}
                </Title>

                <Space h={10} />
                <Group justify="flex-end">
                  <Text>Retry</Text>
                  <ActionIcon
                    loading={query.isLoading}
                    onClick={() =>
                      queryClient.resetQueries({
                        queryKey: ["validateGCS"],
                      })
                    }
                  >
                    <IconRefresh />
                  </ActionIcon>
                </Group>
              </Stack>
            )
          }}
        </QueryWrapper>
      </Stack>
      <Group justify="space-between" pb={20}>
        <Button
          onClick={handleClickBack}
          variant="outline"
          leftSection={<IconArrowLeft />}
        >
          Back
        </Button>
        <Button
          onClick={handleClickImport}
          loading={isImporting}
          disabled={
            !query.data?.isSuccess || !validateHeaders(query.data?.headers)
          }
          rightSection={<IconArrowRight />}
        >
          Import
        </Button>
      </Group>
    </Stack>
  )
}
